import { type Provider as GasProvider } from '@/lib/gas'
import { getOAuthLinks } from '@/lib/oauth'
import { updateDataLayer } from '@/lib/tms'
import GoogleIcon from '../../../public/google.svg'
import { Button } from '@awaze/design-system.components.button'
import { TmsContext } from '@awaze/mfe-common.contexts.tms'
import { TmsEventType } from '@awaze/mfe-common.contexts.tms/dist/DataLayer/DataLayer.types'
import { useContext, useEffect, useState } from 'react'
import Image from 'next/image'

type Provider = {
  label: string
} & GasProvider

type ExternalLoginProvidersProps = {
  providers: Provider[]
}

const getProviderIcon = (providerType: string): React.ReactElement => {
  const type = providerType.toLowerCase()
  // there are currently only google external providers available
  if (type === 'google') {
    return (
      <Image
        alt="google-icon"
        src={GoogleIcon}
        unoptimized
        width={40}
        height={40}
      />
    )
  }
  return <></>
}

const enabledSSOProviders = new Set<Provider['type']>(['google'])
const filterEnabledProviders = ({ type }: { type: string }): boolean => {
  return enabledSSOProviders.has(type.toLowerCase() as Provider['type'])
}

export const ExternalLoginProviders = (
  props: ExternalLoginProvidersProps,
): React.ReactNode => {
  const tmsContext = useContext(TmsContext)

  const [origin, setOrigin] = useState<string>()

  useEffect(() => {
    if (origin !== globalThis.location.origin) {
      setOrigin(globalThis.location.origin)
    }
  }, [setOrigin, origin])

  const providers = origin
    ? getOAuthLinks(origin, props.providers).filter(filterEnabledProviders)
    : []
  const onClick = (provider: (typeof providers)[number]) => () => {
    updateDataLayer(tmsContext, TmsEventType.link, 'login-oauth-click', {
      oauth_provider: provider.type,
    })
    if (globalThis?.location) {
      globalThis.location.href = provider.loginUrl
    }
  }

  return providers.map((provider) => (
    <Button
      key={provider.type}
      text={provider.label}
      type="button"
      icon={getProviderIcon(provider.type)}
      onClick={onClick(provider)}
      data-testid={`login-${provider.type}-button`}
      colourScheme="brand-secondary"
      variant="outline"
    />
  ))
}
